import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Grid,
  Heading,
  Text,
  Icon,
  Card,
  CardHeader,
  CardBody,
  Stack,
  HStack,
  Button,
  Select,
  useColorModeValue,
  Spinner,
  Divider,
  Image,
  Badge,
  Progress,
  SimpleGrid,
  Tag,
  TagLabel,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Avatar,
  AvatarGroup,
  VStack,
} from '@chakra-ui/react';
import {
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiPlusCircle,
  FiRefreshCw,
  FiLayers,
  FiGrid,
  FiBook,
  FiTarget,
  FiStar,
} from 'react-icons/fi';
import { 
  MdOutlineColorLens, 
  MdOutlineStyle,
  MdOutlineArchitecture,
  MdOutlineImageSearch,
  MdOutlineHistory,
} from 'react-icons/md';

// Main Dashboard Component
const DesignDashboard = () => {
  const [timeFrame, setTimeFrame] = useState('month');
  const [isLoading, setIsLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [designStats, setDesignStats] = useState({
    inProgress: 0,
    completed: 0,
    pending: 0,
    review: 0,
  });
  const [designAssets, setDesignAssets] = useState([]);
  const [userFeedback, setUserFeedback] = useState([]);

  useEffect(() => {
    // Simulating API call
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // In a real implementation, this would be an API call
        // const response = await api.get('api/dashboard/design');
        // const data = await response.json();
        
        // Mock data for demo
        setTimeout(() => {
          setDesignStats({
            inProgress: 6,
            completed: 18,
            pending: 4,
            review: 3,
          });
          
          // Mock project data
          setProjects([
            { 
              id: 1, 
              name: 'Redesenho da Tela de Serviços', 
              status: 'Em Progresso', 
              completion: 75, 
              priority: 'Alta',
              deadline: '15/05/2025',
              assignedTo: 'Ana Silva',
              type: 'UI/UX',
            },
            { 
              id: 2, 
              name: 'Atualização do Tema do App', 
              status: 'Em Progresso', 
              completion: 60, 
              priority: 'Alta',
              deadline: '20/05/2025',
              assignedTo: 'Pedro Santos',
              type: 'UI/UX',
            },
            { 
              id: 3, 
              name: 'Criação de Ícones Personalizados', 
              status: 'Em Revisão', 
              completion: 90, 
              priority: 'Média',
              deadline: '10/05/2025',
              assignedTo: 'Lucas Costa',
              type: 'Gráfico',
            },
            { 
              id: 4, 
              name: 'Fotos para Perfis Verificados', 
              status: 'Concluído', 
              completion: 100, 
              priority: 'Média',
              deadline: '05/05/2025',
              assignedTo: 'Carla Oliveira',
              type: 'Foto',
            },
            { 
              id: 5, 
              name: 'Banner para Campanha de Inverno', 
              status: 'Pendente', 
              completion: 0, 
              priority: 'Baixa',
              deadline: '01/06/2025',
              assignedTo: 'Não atribuído',
              type: 'Marketing',
            },
          ]);
          
          // Mock design assets
          setDesignAssets([
            { 
              id: 1, 
              name: 'Logo Principal', 
              type: 'Logo', 
              format: 'SVG, PNG',
              lastUpdate: '24/04/2025',
              usageCount: 128,
              versions: 5,
              thumbnail: 'https://via.placeholder.com/60',
            },
            { 
              id: 2, 
              name: 'Paleta de Cores', 
              type: 'Cores', 
              format: 'CSS, Sketch',
              lastUpdate: '20/04/2025',
              usageCount: 87,
              versions: 3,
              thumbnail: 'https://via.placeholder.com/60',
            },
            { 
              id: 3, 
              name: 'Ícones de Categorias', 
              type: 'Ícones', 
              format: 'SVG, AI',
              lastUpdate: '22/04/2025',
              usageCount: 64,
              versions: 2,
              thumbnail: 'https://via.placeholder.com/60',
            },
            { 
              id: 4, 
              name: 'Elementos de UI', 
              type: 'UI Kit', 
              format: 'Sketch, Figma',
              lastUpdate: '15/04/2025',
              usageCount: 42,
              versions: 8,
              thumbnail: 'https://via.placeholder.com/60',
            },
          ]);
          
          // Mock user feedback
          setUserFeedback([
            {
              id: 1,
              component: 'Página Inicial',
              rating: 4.7,
              feedback: 'Design limpo e intuitivo, fácil navegação',
              date: '29/04/2025',
              user: 'Cliente',
            },
            {
              id: 2,
              component: 'Perfil do Prestador',
              rating: 3.9,
              feedback: 'Bom, mas botões poderiam ser mais visíveis',
              date: '27/04/2025',
              user: 'Contratado',
            },
            {
              id: 3,
              component: 'Processo de Pagamento',
              rating: 4.2,
              feedback: 'Fluxo melhorou bastante com o novo design',
              date: '25/04/2025',
              user: 'Cliente',
            },
            {
              id: 4,
              component: 'Sistema de Mensagens',
              rating: 4.5,
              feedback: 'Interface moderna e responsiva',
              date: '20/04/2025',
              user: 'Contratado',
            },
          ]);
          
          setIsLoading(false);
        }, 1500);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [timeFrame]);

  const handleTimeFrameChange = (e) => {
    setTimeFrame(e.target.value);
  };

  const cardBg = useColorModeValue('white', 'gray.700');
  const lightBg = useColorModeValue('gray.50', 'gray.600');
  
  // Status badge colors
  const getStatusColor = (status) => {
    switch (status) {
      case 'Concluído':
        return 'green';
      case 'Em Progresso':
        return 'blue';
      case 'Em Revisão':
        return 'orange';
      case 'Pendente':
        return 'gray';
      default:
        return 'gray';
    }
  };
  
  // Priority badge colors
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Alta':
        return 'red';
      case 'Média':
        return 'orange';
      case 'Baixa':
        return 'green';
      default:
        return 'gray';
    }
  };
  
  // Type badge colors
  const getTypeColor = (type) => {
    switch (type) {
      case 'UI/UX':
        return 'purple';
      case 'Gráfico':
        return 'cyan';
      case 'Foto':
        return 'blue';
      case 'Marketing':
        return 'orange';
      default:
        return 'gray';
    }
  };

  // Rating color
  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'green.500';
    if (rating >= 4.0) return 'teal.500';
    if (rating >= 3.5) return 'blue.500';
    if (rating >= 3.0) return 'orange.500';
    return 'red.500';
  };

  return (
    <Box w="100%" p={4}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg">Dashboard de Design</Heading>
          <Text color="gray.500">Gestão de projetos e recursos visuais</Text>
        </Box>
        <HStack>
          <Text mr={2}>Período:</Text>
          <Select value={timeFrame} onChange={handleTimeFrameChange} w="150px">
            <option value="day">Hoje</option>
            <option value="week">Esta Semana</option>
            <option value="month">Este Mês</option>
            <option value="quarter">Este Trimestre</option>
            <option value="year">Este Ano</option>
          </Select>
          <Button 
            leftIcon={<FiRefreshCw />} 
            colorScheme="yellow" 
            variant="outline"
            onClick={() => {
              setIsLoading(true);
              setTimeout(() => setIsLoading(false), 1000);
            }}
          >
            Atualizar
          </Button>
        </HStack>
      </Flex>

      {/* Project Status Cards */}
      <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={5} mb={6}>
        <Card bg={cardBg} boxShadow="md">
          <CardBody>
            <Flex justify="space-between" align="center">
              <Box>
                <Text color="gray.500" fontSize="sm">Em Progresso</Text>
                <Text fontSize="3xl" fontWeight="bold">
                  {isLoading ? <Spinner size="sm" /> : designStats.inProgress}
                </Text>
              </Box>
              <Box p={3} borderRadius="full" bg="blue.100">
                <Icon as={FiClock} boxSize={5} color="blue.600" />
              </Box>
            </Flex>
          </CardBody>
        </Card>
        
        <Card bg={cardBg} boxShadow="md">
          <CardBody>
            <Flex justify="space-between" align="center">
              <Box>
                <Text color="gray.500" fontSize="sm">Concluídos</Text>
                <Text fontSize="3xl" fontWeight="bold">
                  {isLoading ? <Spinner size="sm" /> : designStats.completed}
                </Text>
              </Box>
              <Box p={3} borderRadius="full" bg="green.100">
                <Icon as={FiCheckCircle} boxSize={5} color="green.600" />
              </Box>
            </Flex>
          </CardBody>
        </Card>
        
        <Card bg={cardBg} boxShadow="md">
          <CardBody>
            <Flex justify="space-between" align="center">
              <Box>
                <Text color="gray.500" fontSize="sm">Em Revisão</Text>
                <Text fontSize="3xl" fontWeight="bold">
                  {isLoading ? <Spinner size="sm" /> : designStats.review}
                </Text>
              </Box>
              <Box p={3} borderRadius="full" bg="orange.100">
                <Icon as={FiAlertCircle} boxSize={5} color="orange.600" />
              </Box>
            </Flex>
          </CardBody>
        </Card>
        
        <Card bg={cardBg} boxShadow="md">
          <CardBody>
            <Flex justify="space-between" align="center">
              <Box>
                <Text color="gray.500" fontSize="sm">Pendentes</Text>
                <Text fontSize="3xl" fontWeight="bold">
                  {isLoading ? <Spinner size="sm" /> : designStats.pending}
                </Text>
              </Box>
              <Box p={3} borderRadius="full" bg="gray.100">
                <Icon as={FiPlusCircle} boxSize={5} color="gray.600" />
              </Box>
            </Flex>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Current Projects */}
      <Card bg={cardBg} boxShadow="md" mb={6}>
        <CardHeader>
          <Heading size="md">Projetos Atuais</Heading>
        </CardHeader>
        <CardBody px={{ base: 2, md: 4 }}>
          {isLoading ? (
            <Flex justify="center" py={6}>
              <Spinner />
            </Flex>
          ) : (
            <Stack spacing={4} divider={<Divider />}>
              {projects.map(project => (
                <Box key={project.id}>
                  <Flex 
                    direction={{ base: 'column', md: 'row' }} 
                    justify="space-between" 
                    align={{ base: 'flex-start', md: 'center' }}
                    gap={3}
                  >
                    <Box>
                      <Flex align="center" mb={2}>
                        <Heading size="sm">{project.name}</Heading>
                        <Badge ml={2} colorScheme={getStatusColor(project.status)}>
                          {project.status}
                        </Badge>
                        <Badge ml={2} colorScheme={getTypeColor(project.type)} variant="outline">
                          {project.type}
                        </Badge>
                      </Flex>
                      <HStack spacing={4} fontSize="sm" color="gray.500">
                        <Text>Prazo: {project.deadline}</Text>
                        <Text>Prioridade: 
                          <Badge ml={1} colorScheme={getPriorityColor(project.priority)}>
                            {project.priority}
                          </Badge>
                        </Text>
                        <Text>Responsável: {project.assignedTo}</Text>
                      </HStack>
                    </Box>
                    <Box minW="150px">
                      <Text fontSize="sm" mb={1} textAlign="right">
                        {project.completion}% completo
                      </Text>
                      <Progress 
                        value={project.completion} 
                        size="sm" 
                        colorScheme={
                          project.completion === 100 ? 'green' :
                          project.completion > 75 ? 'teal' :
                          project.completion > 50 ? 'blue' :
                          project.completion > 25 ? 'yellow' : 'orange'
                        } 
                        borderRadius="full"
                      />
                    </Box>
                  </Flex>
                </Box>
              ))}
            </Stack>
          )}
        </CardBody>
      </Card>

      {/* Design Assets & User Feedback */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", lg: "3fr 2fr" }} gap={6} mb={6}>
        <Card bg={cardBg} boxShadow="md">
          <CardHeader>
            <Heading size="md">Design System & Assets</Heading>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Tabs colorScheme="yellow" isFitted>
                <TabList>
                  <Tab><Icon as={MdOutlineStyle} mr={2} /> Design System</Tab>
                  <Tab><Icon as={MdOutlineImageSearch} mr={2} /> Assets</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                      <Box p={4} bg={lightBg} borderRadius="md">
                        <Flex align="center" mb={3}>
                          <Icon as={MdOutlineColorLens} color="red.500" mr={2} />
                          <Text fontWeight="bold">Cores</Text>
                        </Flex>
                        <HStack spacing={2} mb={2}>
                          <Box w="30px" h="30px" borderRadius="md" bg="#4bedffff" />
                          <Box w="30px" h="30px" borderRadius="md" bg="#2B6CB0" />
                          <Box w="30px" h="30px" borderRadius="md" bg="#38A169" />
                          <Box w="30px" h="30px" borderRadius="md" bg="#E53E3E" />
                          <Box w="30px" h="30px" borderRadius="md" bg="#718096" />
                        </HStack>
                        <Text fontSize="sm" color="gray.500">5 cores primárias, 12 secundárias</Text>
                      </Box>

                      <Box p={4} bg={lightBg} borderRadius="md">
                        <Flex align="center" mb={3}>
                          <Icon as={FiLayers} color="teal.500" mr={2} />
                          <Text fontWeight="bold">Componentes</Text>
                        </Flex>
                        <Text fontSize="sm" color="gray.500" mb={2}>38 componentes padronizados</Text>
                        <HStack spacing={2}>
                          <Tag size="sm" variant="subtle" colorScheme="teal">Botões</Tag>
                          <Tag size="sm" variant="subtle" colorScheme="blue">Inputs</Tag>
                          <Tag size="sm" variant="subtle" colorScheme="purple">Cards</Tag>
                          <Tag size="sm" variant="subtle" colorScheme="orange">+5</Tag>
                        </HStack>
                      </Box>

                      <Box p={4} bg={lightBg} borderRadius="md">
                        <Flex align="center" mb={3}>
                          <Icon as={MdOutlineArchitecture} color="purple.500" mr={2} />
                          <Text fontWeight="bold">Tipografia</Text>
                        </Flex>
                        <Text fontSize="sm" color="gray.500" mb={2}>2 famílias de fontes</Text>
                        <Stack spacing={1}>
                          <Text fontWeight="bold" fontSize="md">Inter</Text>
                          <Text fontWeight="normal" fontSize="sm">Poppins</Text>
                        </Stack>
                      </Box>

                      <Box p={4} bg={lightBg} borderRadius="md">
                        <Flex align="center" mb={3}>
                          <Icon as={FiGrid} color="blue.500" mr={2} />
                          <Text fontWeight="bold">Layouts</Text>
                        </Flex>
                        <Text fontSize="sm" color="gray.500" mb={2}>Grids responsivos padronizados</Text>
                        <HStack spacing={2} mt={2}>
                          <Tag size="sm" variant="subtle" colorScheme="blue">Mobile</Tag>
                          <Tag size="sm" variant="subtle" colorScheme="teal">Tablet</Tag>
                          <Tag size="sm" variant="subtle" colorScheme="purple">Desktop</Tag>
                        </HStack>
                      </Box>
                    </SimpleGrid>

                    <Button 
                      leftIcon={<FiBook />} 
                      colorScheme="yellow" 
                      variant="outline" 
                      size="sm"
                      mt={5}
                      mx="auto"
                      display="block"
                    >
                      Ver Documentação Completa
                    </Button>
                  </TabPanel>
                  
                  <TabPanel>
                    <Stack spacing={4} divider={<Divider />}>
                      {designAssets.map(asset => (
                        <Flex key={asset.id} justify="space-between" align="center">
                          <HStack>
                            <Image 
                              src={asset.thumbnail} 
                              boxSize="50px" 
                              borderRadius="md" 
                              objectFit="cover" 
                              alt={asset.name} 
                            />
                            <Box>
                              <Text fontWeight="bold">{asset.name}</Text>
                              <HStack spacing={2} fontSize="xs" color="gray.500">
                                <Badge colorScheme="purple">{asset.type}</Badge>
                                <Text>{asset.format}</Text>
                              </HStack>
                            </Box>
                          </HStack>
                          <Box textAlign="right">
                            <Text fontSize="sm">{asset.usageCount} usos</Text>
                            <Text fontSize="xs" color="gray.500">
                              Atualizado: {asset.lastUpdate}
                            </Text>
                          </Box>
                        </Flex>
                      ))}
                    </Stack>

                    <Button 
                      leftIcon={<FiGrid />} 
                      colorScheme="yellow" 
                      variant="outline" 
                      size="sm"
                      mt={5}
                      mx="auto"
                      display="block"
                    >
                      Visualizar Biblioteca
                    </Button>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
          </CardBody>
        </Card>

        <Card bg={cardBg} boxShadow="md">
          <CardHeader>
            <Heading size="md">Feedback dos Usuários</Heading>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Stack spacing={4}>
                {userFeedback.map(feedback => (
                  <Box 
                    key={feedback.id} 
                    p={3} 
                    borderLeft="4px solid" 
                    borderLeftColor={getRatingColor(feedback.rating)}
                    borderRadius="md"
                    bg={lightBg}
                  >
                    <Flex justify="space-between" mb={1}>
                      <Text fontWeight="bold">{feedback.component}</Text>
                      <HStack>
                        <Icon as={FiStar} color={getRatingColor(feedback.rating)} />
                        <Text fontWeight="bold" color={getRatingColor(feedback.rating)}>
                          {feedback.rating}
                        </Text>
                      </HStack>
                    </Flex>
                    <Text fontSize="sm" mb={2}>{feedback.feedback}</Text>
                    <Flex justify="space-between" fontSize="xs" color="gray.500">
                      <Badge colorScheme={feedback.user === 'Cliente' ? 'blue' : 'green'}>
                        {feedback.user}
                      </Badge>
                      <Text>{feedback.date}</Text>
                    </Flex>
                  </Box>
                ))}

                <Box pt={2}>
                  <Flex justify="space-between" align="center">
                    <Text fontSize="sm" fontWeight="bold">Avaliação Média:</Text>
                    <HStack>
                      <Icon as={FiStar} color="yellow.500" />
                      <Text fontWeight="bold">4.3/5.0</Text>
                    </HStack>
                  </Flex>

                  <Divider my={3} />

                  <Box>
                    <Text fontSize="sm" fontWeight="bold" mb={2}>Áreas para Melhorias:</Text>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      <Tag size="sm" variant="subtle" colorScheme="blue">
                        <TagLabel>Contraste</TagLabel>
                      </Tag>
                      <Tag size="sm" variant="subtle" colorScheme="orange">
                        <TagLabel>Tamanho de Fonte</TagLabel>
                      </Tag>
                      <Tag size="sm" variant="subtle" colorScheme="purple">
                        <TagLabel>Acessibilidade</TagLabel>
                      </Tag>
                    </Stack>
                  </Box>
                </Box>
              </Stack>
            )}
          </CardBody>
        </Card>
      </Grid>

      {/* Design Activities & Timeline */}
      <Card bg={cardBg} boxShadow="md">
        <CardHeader>
          <Heading size="md">Visão da Equipe de Design</Heading>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <Flex justify="center" py={6}>
              <Spinner />
            </Flex>
          ) : (
            <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }} gap={6}>
              <Box>
                <Flex align="center" mb={4}>
                  <Icon as={MdOutlineHistory} mr={2} color="purple.500" />
                  <Text fontWeight="bold">Últimas Atividades</Text>
                </Flex>
                <VStack align="stretch" spacing={3}>
                  <HStack align="flex-start" spacing={3}>
                    <Box position="relative">
                      <Avatar size="sm" name="Ana Silva" bg="blue.400" />
                      <Box 
                        position="absolute" 
                        bottom="0" 
                        right="0" 
                        w="10px" 
                        h="10px" 
                        bg="green.400" 
                        borderRadius="full" 
                        border="2px solid white" 
                      />
                    </Box>
                    <Box flex="1">
                      <Text fontSize="sm">
                        <Text as="span" fontWeight="bold">Ana Silva</Text>
                        {' '}atualizou as telas de perfil do prestador
                      </Text>
                      <Text fontSize="xs" color="gray.500">2 horas atrás</Text>
                    </Box>
                  </HStack>
                  
                  <HStack align="flex-start" spacing={3}>
                    <Avatar size="sm" name="Pedro Santos" bg="green.400" />
                    <Box flex="1">
                      <Text fontSize="sm">
                        <Text as="span" fontWeight="bold">Pedro Santos</Text>
                        {' '}enviou 3 propostas para nova tela de filtros
                      </Text>
                      <Text fontSize="xs" color="gray.500">5 horas atrás</Text>
                    </Box>
                  </HStack>
                  
                  <HStack align="flex-start" spacing={3}>
                    <Avatar size="sm" name="Lucas Costa" bg="purple.400" />
                    <Box flex="1">
                      <Text fontSize="sm">
                        <Text as="span" fontWeight="bold">Lucas Costa</Text>
                        {' '}finalizou ícones para categorias de serviços
                      </Text>
                      <Text fontSize="xs" color="gray.500">7 horas atrás</Text>
                    </Box>
                  </HStack>
                  
                  <HStack align="flex-start" spacing={3}>
                    <Avatar size="sm" name="Carla Oliveira" bg="red.400" />
                    <Box flex="1">
                      <Text fontSize="sm">
                        <Text as="span" fontWeight="bold">Carla Oliveira</Text>
                        {' '}atualizou a biblioteca de componentes
                      </Text>
                      <Text fontSize="xs" color="gray.500">Ontem</Text>
                    </Box>
                  </HStack>
                </VStack>
              </Box>
              
              <Box>
                <Flex align="center" mb={4}>
                  <Icon as={FiTarget} mr={2} color="teal.500" />
                  <Text fontWeight="bold">Objetivos do Trimestre</Text>
                </Flex>
                
                <Stack spacing={4}>
                  <Box>
                    <Flex justify="space-between" mb={1}>
                      <Text fontSize="sm" fontWeight="medium">Redesenho da Experiência Mobile</Text>
                      <Text fontSize="sm">75%</Text>
                    </Flex>
                    <Progress value={75} size="sm" colorScheme="teal" borderRadius="full" />
                  </Box>
                  
                  <Box>
                    <Flex justify="space-between" mb={1}>
                      <Text fontSize="sm" fontWeight="medium">Atualizar Identidade Visual</Text>
                      <Text fontSize="sm">40%</Text>
                    </Flex>
                    <Progress value={40} size="sm" colorScheme="yellow" borderRadius="full" />
                  </Box>
                  
                  <Box>
                    <Flex justify="space-between" mb={1}>
                      <Text fontSize="sm" fontWeight="medium">Design System Completo</Text>
                      <Text fontSize="sm">90%</Text>
                    </Flex>
                    <Progress value={90} size="sm" colorScheme="green" borderRadius="full" />
                  </Box>
                  
                  <Box>
                    <Flex justify="space-between" mb={1}>
                      <Text fontSize="sm" fontWeight="medium">Melhorias de Acessibilidade</Text>
                      <Text fontSize="sm">30%</Text>
                    </Flex>
                    <Progress value={30} size="sm" colorScheme="orange" borderRadius="full" />
                  </Box>
                </Stack>
                
                <HStack mt={5} justify="flex-end">
                  <AvatarGroup size="sm" max={3}>
                    <Avatar name="Ana Silva" bg="blue.400" />
                    <Avatar name="Pedro Santos" bg="green.400" />
                    <Avatar name="Lucas Costa" bg="purple.400" />
                    <Avatar name="Carla Oliveira" bg="red.400" />
                    <Avatar name="João Gomes" bg="teal.400" />
                  </AvatarGroup>
                  <Text fontSize="sm" color="gray.500">5 designers ativos</Text>
                </HStack>
              </Box>
            </Grid>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};

export default DesignDashboard;